<template>
    <div class="gift-card-overlay fixed inset-0 z-50 flex flex-col bg-white">

        <!-- Status pages (success / cancelled / failed) -->
        <template v-if="page_status() === 'success' || page_status() === 'cancelled' || page_status() === 'failed'">
            <div class="shrink-0 bg-white border-b border-grey-lightest flex items-center px-6 py-4">
                <div class="flex items-center gap-2.5">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-action" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                    <span class="text-heading font-bold text-sm">Gift Cards</span>
                </div>
            </div>

            <div class="flex-1 overflow-y-auto bg-grey-lightest px-6 py-8">
                <div class="bg-white rounded-2xl px-10 py-12 max-w-md w-full mx-auto flex flex-col items-center text-center gap-6 shadow-sm my-4">

                    <!-- Success -->
                    <template v-if="page_status() === 'success'">

                        <!-- Loading -->
                        <template v-if="return_loading">
                            <svg class="animate-spin h-8 w-8 text-action" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 512 512">
                                <path fill="currentColor" d="M440.65 12.57l4 82.77A247.16 247.16 0 0 0 255.83 8C134.73 8 33.91 94.92 12.29 209.82A12 12 0 0 0 24.09 224h49.05a12 12 0 0 0 11.67-9.26 175.91 175.91 0 0 1 317-56.94l-101.46-4.86a12 12 0 0 0-12.57 12v47.41a12 12 0 0 0 12 12H500a12 12 0 0 0 12-12V12a12 12 0 0 0-12-12h-47.37a12 12 0 0 0-11.98 12.57zM255.83 432a175.61 175.61 0 0 1-146-77.8l101.8 4.87a12 12 0 0 0 12.57-12v-47.4a12 12 0 0 0-12-12H12a12 12 0 0 0-12 12V500a12 12 0 0 0 12 12h47.35a12 12 0 0 0 12-12.6l-4.15-82.57A247.17 247.17 0 0 0 255.83 504c121.11 0 221.93-86.92 243.55-201.82a12 12 0 0 0-11.8-14.18h-49.05a12 12 0 0 0-11.67 9.26A175.86 175.86 0 0 1 255.83 432z"></path>
                            </svg>
                            <p class="text-grey-default text-sm">Confirming your payment...</p>
                        </template>

                        <!-- Paid -->
                        <template v-else-if="return_paid">
                            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h1 class="text-heading font-bold text-2xl mb-2">Gift card purchased!</h1>
                                <p class="text-grey-dark text-sm leading-relaxed">
                                    Your gift card has been confirmed. We've sent it to <strong>{{ get_return_email() }}</strong> - forward it on to your lucky recipient.
                                </p>
                            </div>
                            <div class="w-full bg-grey-lightest rounded-xl px-6 py-5 flex flex-col gap-3 text-left">
                                <div class="flex justify-between items-center">
                                    <span class="text-grey-default font-semibold uppercase tracking-wide text-xs">Amount</span>
                                    <span class="text-heading font-bold">{{ get_return_amount() }}</span>
                                </div>
                                <div class="flex justify-between items-center">
                                    <span class="text-grey-default font-semibold uppercase tracking-wide text-xs">Sent to</span>
                                    <span class="text-heading font-bold text-sm">{{ get_return_email() }}</span>
                                </div>
                                <div v-if="get_transaction_ref()" class="flex justify-between items-center">
                                    <span class="text-grey-default font-semibold uppercase tracking-wide text-xs">Reference</span>
                                    <span class="text-heading font-bold text-sm">{{ get_transaction_ref() }}</span>
                                </div>
                            </div>
                            <a v-if="return_booking?.documents?.find((d: any) => d.name === 'Booking Statement')"
                                :href="return_booking.documents.find((d: any) => d.name === 'Booking Statement').url"
                                target="_blank"
                                class="w-full flex items-center justify-center gap-2 border border-grey-lighter rounded-lg px-4 py-3 text-sm font-semibold text-heading hover:border-action hover:text-action transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                </svg>
                                Download booking statement
                            </a>
                            <a href="/" class="button button-pink-book w-full text-center">Back to homepage</a>
                        </template>

                        <!-- Not paid -->
                        <template v-else>
                            <div class="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-aat-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                                </svg>
                            </div>
                            <div>
                                <h1 class="text-heading font-bold text-2xl mb-2">Payment unsuccessful</h1>
                                <p class="text-grey-dark text-sm leading-relaxed">
                                    Your payment didn't go through and you have not been charged. Please try again or contact us if the problem persists.
                                </p>
                            </div>
                            <a href="/?view=giftcard" class="button button-pink-book w-full text-center">Try again</a>
                            <a href="/" class="w-full text-center py-3 text-sm font-semibold text-grey-dark hover:text-heading transition-colors">Back to homepage</a>
                        </template>

                    </template>                    

                </div>
            </div>
        </template>

        <!-- Normal gift card UI -->
        <template v-else>

            <!-- Top bar -->
            <div class="shrink-0 bg-white border-b border-grey-lightest flex items-center justify-between px-6 py-4 z-10">
                <div class="flex items-center gap-2.5">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-action" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                    <span class="text-heading font-bold text-sm">Gift Cards</span>
                </div>
                <button type="button" class="flex items-center gap-2 text-sm text-grey-default hover:text-heading transition-colors group" v-on:click="close()">
                    <span>Close</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 group-hover:rotate-90 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Body -->
            <div class="flex flex-1 min-h-0 bg-grey-lightest">

                <div class="hidden lg:flex lg:w-5/12 xl:w-2/5 shrink-0 flex-col p-6 gap-4">
                    <div class="relative rounded-2xl overflow-hidden flex-1">
                        <div
                            class="absolute inset-0 bg-cover bg-center"
                            :style="card_image ? 'background-image: url(' + card_image + ')' : ''"
                            :class="!card_image ? 'bg-grey-lighter' : ''"
                        ></div>
                    </div>
                    <div class="shrink-0 bg-white rounded-2xl px-6 py-5">
                        <p class="text-xs font-semibold uppercase tracking-widest text-grey-default mb-2">Gift Card</p>
                        <h1 class="text-xl font-bold text-heading leading-tight mb-1">{{ card_title }}</h1>
                        <p v-if="card_description" class="text-grey-dark text-sm leading-relaxed">{{ card_description }}</p>
                        <transition name="fade-up">
                            <div v-if="effective_amount" class="mt-4 pt-4 border-t border-grey-lightest">
                                <p class="text-grey-default text-xs uppercase tracking-widest font-semibold mb-1">Gift card value</p>
                                <p class="text-3xl font-bold text-heading tracking-tight">{{ formatted_amount }}</p>
                                <p class="text-grey-default text-xs mt-1">{{ currency_code }} &middot; includes all taxes</p>
                            </div>
                        </transition>
                    </div>
                </div>

                <!-- Right: form -->
                <div class="flex-1 flex flex-col min-h-0 bg-white lg:rounded-tl-2xl">
                    <div class="flex-1 overflow-y-auto">
                        <div class="max-w-md mx-auto px-8 pt-2 md:pt-12 pb-6 flex flex-col gap-10">

                            <div class="lg:hidden">
                                <h1 class="text-heading font-bold text-2xl">{{ card_title }}</h1>
                                <p v-if="card_description" class="text-grey-dark text-sm mt-1.5">{{ card_description }}</p>
                            </div>

                            <!-- Amount picker -->
                            <div class="flex flex-col items-center gap-6">
                                <p class="text-xs font-semibold uppercase tracking-widest text-grey-default">Select Amount</p>

                                <div class="flex items-center gap-5 w-full justify-center">
                                    <button
                                        type="button"
                                        class="w-11 h-11 rounded-full border-2 flex items-center justify-center text-xl font-bold transition-all duration-150 shrink-0"
                                        :class="can_go_prev
                                            ? 'border-grey-light text-heading hover:border-action hover:text-action'
                                            : 'border-grey-lightest text-grey-lighter cursor-not-allowed'"
                                        v-on:click="step_denomination(-1)"
                                        :disabled="!can_go_prev"
                                    >&#8722;</button>

                                    <transition name="amount-swap" mode="out-in">
                                        <div v-if="!is_custom" :key="'preset-' + selected_denomination" class="w-44 text-center">
                                            <span class="text-5xl font-bold text-heading tracking-tight">{{ formatted_amount }}</span>
                                        </div>
                                        <div v-else key="custom" class="w-44 flex items-center justify-center border-b-2 border-action pb-1">
                                            <span class="text-3xl font-bold text-grey-light mr-1">{{ currency_symbol }}</span>
                                            <input
                                                ref="custom_input"
                                                type="number"
                                                min="5"
                                                max="2000"
                                                class="text-4xl font-bold text-heading text-center bg-transparent focus:outline-none w-32"
                                                v-model="custom_amount_raw"
                                                v-on:input="on_custom_input"
                                                placeholder="0"
                                            />
                                        </div>
                                    </transition>

                                    <button
                                        type="button"
                                        class="w-11 h-11 rounded-full border-2 flex items-center justify-center text-xl font-bold transition-all duration-150 shrink-0"
                                        :class="can_go_next
                                            ? 'border-grey-light text-heading hover:border-action hover:text-action'
                                            : 'border-grey-lightest text-grey-lighter cursor-not-allowed'"
                                        v-on:click="step_denomination(1)"
                                        :disabled="!can_go_next"
                                    >&#43;</button>
                                </div>

                                <div class="flex flex-wrap justify-center gap-2">
                                    <button
                                        v-for="amount in denominations"
                                        :key="amount"
                                        type="button"
                                        class="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150"
                                        :class="!is_custom && selected_denomination === amount
                                            ? 'bg-action border-action text-white'
                                            : 'bg-white border-grey-lighter text-grey-dark hover:border-grey-default'"
                                        v-on:click="select_denomination(amount)"
                                    >{{ format_currency(amount) }}</button>
                                    <button
                                        type="button"
                                        class="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150"
                                        :class="is_custom
                                            ? 'bg-action border-action text-white'
                                            : 'bg-white border-grey-lighter text-grey-dark hover:border-grey-default'"
                                        v-on:click="enter_custom()"
                                    >Custom</button>
                                </div>

                                <p v-if="has_error('amount')" class="text-aat-red text-xs -mt-2 text-center">{{ errors.amount }}</p>
                            </div>

                            <hr class="border-grey-lightest" />

                            <!-- Name -->
                            <div class="flex flex-col gap-4">
                                <div>
                                    <h3 class="text-heading font-bold text-base">Your details</h3>
                                    <p class="text-grey-default text-sm mt-0.5">Who is purchasing this gift card?</p>
                                </div>
                                <div class="flex gap-3">
                                    <div class="flex-1">
                                        <label class="block text-xs font-semibold text-grey-dark uppercase tracking-wider mb-1.5">First name</label>
                                        <input
                                            type="text"
                                            autocomplete="off"
                                            class="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none transition-colors"
                                            :class="has_error('first_name') ? 'border-red-500 bg-red-50' : 'border-grey-lighter focus:border-action'"
                                            v-model="first_name"
                                            placeholder="First name"
                                        />
                                        <p v-if="has_error('first_name')" class="text-aat-red text-xs mt-1.5">{{ errors.first_name }}</p>
                                    </div>
                                    <div class="flex-1">
                                        <label class="block text-xs font-semibold text-grey-dark uppercase tracking-wider mb-1.5">Last name</label>
                                        <input
                                            type="text"
                                            autocomplete="off"
                                            class="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none transition-colors"
                                            :class="has_error('last_name') ? 'border-red-500 bg-red-50' : 'border-grey-lighter focus:border-action'"
                                            v-model="last_name"
                                            placeholder="Last name"
                                        />
                                        <p v-if="has_error('last_name')" class="text-aat-red text-xs mt-1.5">{{ errors.last_name }}</p>
                                    </div>
                                </div>
                            </div>

                            <hr class="border-grey-lightest" />

                            <!-- Email -->
                            <div class="flex flex-col gap-3">
                                <div>
                                    <h3 class="text-heading font-bold text-base">Your Email</h3>
                                    <p class="text-grey-default text-sm mt-0.5">We'll send your gift card here - so you can forward it on to the recipient</p>
                                </div>
                                <input
                                    type="email"
                                    autocomplete="off"
                                    class="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none transition-colors"
                                    :class="has_error('email') ? 'border-red-500 bg-red-50' : 'border-grey-lighter focus:border-action'"
                                    v-model="recipient_email"
                                    placeholder="your@email.com"
                                />
                                <p v-if="has_error('email')" class="text-aat-red text-xs">{{ errors.email }}</p>
                            </div>

                            <div class="flex flex-col gap-3">
                                <label class="block text-xs font-semibold text-grey-dark uppercase tracking-wider">Confirm email</label>
                                <input
                                    type="email"
                                    autocomplete="off"
                                    class="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none transition-colors"
                                    :class="has_error('email_confirm') ? 'border-red-500 bg-red-50' : 'border-grey-lighter focus:border-action'"
                                    v-model="recipient_email_confirm"
                                    placeholder="Confirm your email address"
                                />
                                <p v-if="has_error('email_confirm')" class="text-aat-red text-xs">{{ errors.email_confirm }}</p>
                            </div>

                            <p v-if="submitted && !is_valid" class="text-aat-red text-xs text-center -mt-4">
                                Please fix the errors above.
                            </p>

                        </div>
                    </div>

                    <!-- Sticky footer -->
                    <div class="shrink-0 bg-white border-t border-grey-lightest px-8 py-4">
                        <div class="max-w-md mx-auto flex items-center gap-5">
                            <div class="shrink-0 min-w-[80px]">
                                <p v-if="effective_amount">
                                    <span class="block text-xs text-grey-default mb-0.5">Total</span>
                                    <span class="text-xl font-bold text-heading">{{ formatted_amount }}</span>
                                </p>
                                <p v-else class="text-xs text-grey-default leading-snug">Select a value<br/>to continue</p>
                            </div>
                            <button type="button" :disabled="true" v-if="is_processing" class="button button-pink-book flex-1">
                                <svg class="animate-spin h-5 w-5 mr-3 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 512 512" stroke="currentColor">
                                    <path fill="currentColor" d="M440.65 12.57l4 82.77A247.16 247.16 0 0 0 255.83 8C134.73 8 33.91 94.92 12.29 209.82A12 12 0 0 0 24.09 224h49.05a12 12 0 0 0 11.67-9.26 175.91 175.91 0 0 1 317-56.94l-101.46-4.86a12 12 0 0 0-12.57 12v47.41a12 12 0 0 0 12 12H500a12 12 0 0 0 12-12V12a12 12 0 0 0-12-12h-47.37a12 12 0 0 0-11.98 12.57zM255.83 432a175.61 175.61 0 0 1-146-77.8l101.8 4.87a12 12 0 0 0 12.57-12v-47.4a12 12 0 0 0-12-12H12a12 12 0 0 0-12 12V500a12 12 0 0 0 12 12h47.35a12 12 0 0 0 12-12.6l-4.15-82.57A247.17 247.17 0 0 0 255.83 504c121.11 0 221.93-86.92 243.55-201.82a12 12 0 0 0-11.8-14.18h-49.05a12 12 0 0 0-11.67 9.26A175.86 175.86 0 0 1 255.83 432z"></path>
                                </svg>
                            </button>
                            <button type="button" v-else class="button button-pink-book flex-1" v-on:click="proceed()">
                                Proceed to payment
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </template>

    </div>
</template>

<style scoped>
    .fade-up-enter-active, .fade-up-leave-active {
        transition: opacity 0.3s ease, transform 0.3s ease;
    }
    .fade-up-enter-from, .fade-up-leave-to {
        opacity: 0;
        transform: translateY(8px);
    }
    .amount-swap-enter-active, .amount-swap-leave-active {
        transition: opacity 0.15s ease, transform 0.15s ease;
    }
    .amount-swap-enter-from {
        opacity: 0;
        transform: translateY(6px);
    }
    .amount-swap-leave-to {
        opacity: 0;
        transform: translateY(-6px);
    }
    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
    input[type=number] { -moz-appearance: textfield; }
</style>

<script lang="ts" src="./gift_card.ts"></script>